import { FormEvent, useEffect, useState } from "react";
import { faEnvelope, faKey, faXmark } from "@fortawesome/free-solid-svg-icons";
import { faEye, faEyeSlash } from "@fortawesome/free-regular-svg-icons";
import { gql, useLazyQuery } from "@apollo/client";

import { AuthCardLayout } from "./LoginCard";
import { AuthTabs } from "./AuthTabs";
import { Button } from "../design-system/button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { HeaderLG } from "../design-system/typography";
import { Link } from "react-router-dom";
import { LoginCardInput } from "./LoginCardInput";
import { getPortalLandingPath } from "../util/portalRoutes";
import { safeInternalPath } from "../util/safeInternalPath";
import { signupEnabled } from "../util/signupFlag";
import { useAuth0 } from "@auth0/auth0-react";

export const GET_USER_AUTH_INFO = gql`
  query GetUserAuthInfo($username: String!) {
    userAuthInfo(username: $username) {
      authProvider
      role
    }
  }
`;

/** Rebranded login card: email, then an Auth0 redirect, a password, or a magic link. */
export default function NewLoginPage() {
  const { loginWithRedirect } = useAuth0();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [message, setMessage] = useState("");
  const [isLocked, setIsLocked] = useState(false);
  const [isWarning, setIsWarning] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);
  const [step, setStep] = useState<"email_entry" | "password_entry">(
    "email_entry",
  );

  const [getUserAuthInfo, { loading: credsLoading }] = useLazyQuery(
    GET_USER_AUTH_INFO,
    {
      fetchPolicy: "no-cache",
    },
  );

  // Clear stale session state so useAuth falls back to roles[0] after login.
  useEffect(() => {
    sessionStorage.removeItem("authRedirect");
    localStorage.removeItem("activeRole");
  }, []);

  const trimmedEmail = email.trim();

  const query = new URLSearchParams(window.location.search);
  const redirectTo = query.get("redirect_to");

  const loginWithAuth0 = (role: string) => {
    loginWithRedirect({
      appState: {
        returnTo: safeInternalPath(redirectTo) ?? getPortalLandingPath(role),
      },
      authorizationParams: {
        login_hint: trimmedEmail,
      },
    });
  };

  const handleEmailSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setMessage("");

    if (!trimmedEmail) {
      setMessage("Enter your email address to continue.");
      return;
    }

    const { data, error } = await getUserAuthInfo({
      variables: { username: trimmedEmail },
    });
    if (error) {
      setMessage(
        error.networkError
          ? "Failed to connect. Please check your internet connection and try again."
          : "Failed to determine login method. Please try again.",
      );
      return;
    }
    if (data?.userAuthInfo?.authProvider === "AUTH0") {
      loginWithAuth0(data.userAuthInfo.role);
    } else if (data?.userAuthInfo?.authProvider === "SPREE") {
      setStep("password_entry");
    }
  };

  const authenticate = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setMessage("");
    setIsLocked(false);
    setIsWarning(false);
    setAuthLoading(true);

    const startLoadingTime = Date.now();
    const minLoadingDuration = 500;

    fetch("/graphql/login", {
      method: "POST",
      body: JSON.stringify({
        username: trimmedEmail,
        password: password,
      }),
      headers: {
        "Content-Type": "application/json",
      },
    })
      .then((res) => {
        if (res.redirected) {
          // Prefer the originally requested page, else the backend's default.
          window.location.href = safeInternalPath(redirectTo) ?? res.url;
          return; // if being redirected, the result is not json
        }
        return res.json();
      })
      .then((res) => {
        // ensure loading state shows for at least minLoadingDuration
        const elapsedTime = Date.now() - startLoadingTime;
        const remainingTime = Math.max(0, minLoadingDuration - elapsedTime);

        setTimeout(() => {
          if (!res) return;

          if (res.locked) {
            setIsLocked(true);
            setMessage(res.data);
          } else if (res.warning) {
            setIsWarning(true);
            setMessage(res.data);
          } else {
            setMessage(res.data);
          }

          setAuthLoading(false);
        }, remainingTime);
      })
      .catch(() => {
        setMessage("Login failed. Please try again.");
        setAuthLoading(false);
      });
  };

  const sendMagicLink = () => {
    window.location.href =
      "/graphql/passwordless-auth?email=" + encodeURIComponent(trimmedEmail);
  };

  const returnToEmailEntry = () => {
    setStep("email_entry");
    setPassword("");
    setShowPassword(false);
    setMessage("");
    setIsLocked(false);
    setIsWarning(false);
  };

  const isBusy = authLoading || credsLoading;

  const getButtonText = () => {
    if (authLoading) {
      return "Logging in...";
    }
    if (credsLoading) {
      return "Checking...";
    }
    return step === "email_entry" ? "Continue" : "Log In";
  };

  return (
    // The tabs sit 24px under the logo and 40px above the form.
    <AuthCardLayout className={signupEnabled() ? "tw-gap-[24px]" : undefined}>
      {signupEnabled() && (
        <div className="tw-w-full tw-pb-[16px]">
          <AuthTabs selected="login" />
        </div>
      )}
      <form
        className="tw-flex tw-w-full tw-flex-col tw-gap-[24px]"
        onSubmit={step === "email_entry" ? handleEmailSubmit : authenticate}
      >
        <HeaderLG>Welcome back to Verse Medical.</HeaderLG>

        <div className="tw-flex tw-flex-col tw-gap-[12px]">
          <LoginCardInput
            id="login-email"
            icon={faEnvelope}
            type="text"
            placeholder="Email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            readOnly={step === "password_entry"}
            trailing={
              step === "password_entry" ? (
                <button
                  type="button"
                  aria-label="Use a different email"
                  className="tw-flex tw-h-[16px] tw-w-[16px] tw-items-center tw-justify-center tw-border-none tw-bg-transparent tw-p-0 tw-text-verse-icon-disabled"
                  onClick={returnToEmailEntry}
                >
                  <FontAwesomeIcon icon={faXmark} />
                </button>
              ) : null
            }
          />

          {step === "password_entry" && (
            <div className="tw-flex tw-flex-col tw-gap-[8px]">
              <LoginCardInput
                id="login-password"
                icon={faKey}
                type={showPassword ? "text" : "password"}
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                hasError={isLocked || isWarning}
                autoFocus
                trailing={
                  <button
                    type="button"
                    aria-label={
                      showPassword ? "Hide password" : "Show password"
                    }
                    className="tw-flex tw-h-[16px] tw-w-[16px] tw-items-center tw-justify-center tw-border-none tw-bg-transparent tw-p-0 tw-text-verse-icon-primary"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                  </button>
                }
              />
              <Link
                to="/password/request-password-reset"
                className="tw-font-sohne tw-text-[14px] tw-text-verse-text-brand tw-underline"
              >
                Forgot your password?
              </Link>
            </div>
          )}
        </div>

        {message && (
          <div
            className="tw-font-sohne tw-text-[14px] tw-text-verse-text-critical"
            role="alert"
          >
            {isLocked && (
              <div className="tw-font-semibold">Too many login attempts</div>
            )}
            {isWarning && (
              <div className="tw-font-semibold">Invalid password</div>
            )}
            {message}
          </div>
        )}

        <div className="tw-flex tw-flex-col tw-gap-[12px]">
          <Button
            id="login-submit"
            type="submit"
            data-testid="login-submit-button"
            disabled={isBusy}
            colorScheme="tide"
            size="lg"
            className="tw-w-full"
          >
            {getButtonText()}
          </Button>

          {step === "password_entry" && (
            <p className="tw-m-0 tw-text-center tw-font-sohne tw-text-[14px] tw-text-verse-text-tertiary">
              Log in with a one-time link instead.{" "}
              <button
                type="button"
                className="tw-border-none tw-bg-transparent tw-p-0 tw-text-verse-text-brand tw-underline"
                onClick={sendMagicLink}
              >
                Send email
              </button>
            </p>
          )}
        </div>
      </form>
    </AuthCardLayout>
  );
}
